import MuiTypography from "@mui/material/Typography";
import { cleanProps } from "../internal/cleanProps";
import type { TypographyProps, TypographyVariant } from "../types";

// Maps this adapter's semantic variant names (matching Aoife's token
// table verbatim) onto the Material UI variant keys `theme.ts` assigns
// those token values to. Kept local to this file — feature modules never
// see a Material UI variant name.
const MUI_VARIANT: Record<TypographyVariant, "h1" | "h2" | "h3" | "h4" | "overline" | "subtitle1" | "body1" | "body2" | "caption" | "button"> = {
  display: "h1",
  heading1: "h2",
  heading2: "h3",
  heading3: "h4",
  eyebrow: "overline",
  bodyLarge: "subtitle1",
  body: "body1",
  bodySmall: "body2",
  helper: "caption",
  action: "button",
};

/**
 * Containment note (types.ts): `as` fixes the upstream `RootComponent`
 * generic to a closed element union; nothing else upstream-generic is
 * forwarded.
 */
export function Typography({ variant, as, tone, id, children, testId, dateTime }: Readonly<TypographyProps>) {
  return (
    <MuiTypography
      variant={MUI_VARIANT[variant]}
      color={tone === "muted" ? "textSecondary" : "textPrimary"}
      {...cleanProps({
        component: as,
        id,
        "data-testid": testId,
        // Only ever meaningful when `as === "time"` -- MUI forwards an
        // unrecognised prop straight to the underlying host element, so
        // this becomes the native `<time>` element's `datetime` attribute.
        ...(as === "time" ? { dateTime } : {}),
      })}
    >
      {children}
    </MuiTypography>
  );
}
